import React from 'react';
import type { MatterDetailed } from '../../../../../types/matters';
import MatterBudgetCard from '../../../../billing/components/MatterBudgetCard';
import AllocationEditor from '../../../../billing/components/AllocationEditor';

interface MatterBillingStats {
  unbilledTimeAmount: number;
  totalHours: number;
  timeEntryCount: number;
  unbilledExpenses: number;
  expenseCount: number;
  totalBilled: number;
  billsOutstanding: number;
  billCount: number;
  trustBalance: number;
  trustTransactionCount: number;
}

interface BillingOverviewSectionProps {
  matter: MatterDetailed;
  stats: MatterBillingStats;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const BillingOverviewSection: React.FC<BillingOverviewSectionProps> = ({ matter, stats }) => {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="text-blue-600 dark:text-blue-400 text-sm font-medium">Time (WIP)</div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100 tabular-nums">
            {formatCurrency(stats.unbilledTimeAmount)}
          </div>
          <div className="text-xs text-blue-500 dark:text-blue-400 mt-1">
            {stats.totalHours.toFixed(1)} hrs ({stats.timeEntryCount} entries)
          </div>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
          <div className="text-amber-600 dark:text-amber-400 text-sm font-medium">Expenses (WIP)</div>
          <div className="text-2xl font-bold text-amber-900 dark:text-amber-100 tabular-nums">
            {formatCurrency(stats.unbilledExpenses)}
          </div>
          <div className="text-xs text-amber-500 dark:text-amber-400 mt-1">
            {stats.expenseCount} expense{stats.expenseCount !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <div className="text-green-600 dark:text-green-400 text-sm font-medium">Total Billed</div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100 tabular-nums">
            {formatCurrency(stats.totalBilled)}
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="text-purple-600 dark:text-purple-400 text-sm font-medium">Outstanding</div>
          <div className="text-2xl font-bold text-purple-900 dark:text-purple-100 tabular-nums">
            {formatCurrency(stats.billsOutstanding)}
          </div>
          <div className="text-xs text-purple-500 dark:text-purple-400 mt-1">
            {stats.billCount} invoice{stats.billCount !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-200 dark:border-teal-800">
          <div className="text-teal-600 dark:text-teal-400 text-sm font-medium">Trust Balance</div>
          <div className="text-2xl font-bold text-teal-900 dark:text-teal-100 tabular-nums">
            {formatCurrency(stats.trustBalance)}
          </div>
          <div className="text-xs text-teal-500 dark:text-teal-400 mt-1">
            {stats.trustTransactionCount} transaction{stats.trustTransactionCount !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Billing Configuration */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Billing Configuration</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Billing Method</label>
            <p className="text-gray-900 dark:text-white">{matter.billingMethod}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Billable Status</label>
            <p className="text-gray-900 dark:text-white">{matter.isBillable ? 'Billable' : 'Non-billable'}</p>
          </div>
        </div>
      </div>

      {/* Matter Budget */}
      <MatterBudgetCard matterId={matter.id} />

      {/* Split Billing Allocation */}
      <AllocationEditor matterId={matter.id} />
    </div>
  );
};

export default BillingOverviewSection;
